import React, { useState, useEffect, useCallback, useRef } from 'react';
import { incomeAPI, householdAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { formatAmount } from '../../utils/formatters';
import { formatDate } from '../../utils/dateUtils';
import Toggle from '../common/Toggle';
import OwnershipBadge from '../common/OwnershipBadge';
import CustomSelect from '../common/CustomSelect';
import TextInput from '../common/TextInput';
import CurrencyInput from '../common/CurrencyInput';
import DatePicker from '../DatePicker';
import BaseModal from '../common/BaseModal';
import '../../styles/ManageIncomeModal.css';
import {
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Loader2,
  Wallet,
  AlertTriangle,
  CheckCircle,
  Image,
  X,
  RefreshCw,
  ChevronDown,
  User,
  Home
} from 'lucide-react';

import useLogoFetch from '../../hooks/useLogoFetch';

// Domain mapping for common employers/income sources
const EMPLOYER_DOMAIN_MAP = {
  // Tech
  'google': 'google.com',
  'microsoft': 'microsoft.com',
  'amazon': 'amazon.com',
  'apple': 'apple.com',
  'meta': 'meta.com',
  'facebook': 'meta.com',
  'netflix': 'netflix.com',
  'salesforce': 'salesforce.com',
  'oracle': 'oracle.com',
  'ibm': 'ibm.com',
  'intel': 'intel.com',
  'cisco': 'cisco.com',
  'adobe': 'adobe.com',
  'nvidia': 'nvidia.com',
  'paypal': 'paypal.com',
  'stripe': 'stripe.com',
  'shopify': 'shopify.com',
  'uber': 'uber.com',
  'lyft': 'lyft.com',
  'airbnb': 'airbnb.com',
  'twitter': 'x.com',
  'linkedin': 'linkedin.com',
  'slack': 'slack.com',
  'zoom': 'zoom.us',
  'dropbox': 'dropbox.com',
  'spotify': 'spotify.com',
  
  // Finance
  'jpmorgan': 'jpmorgan.com',
  'chase': 'chase.com',
  'bank of america': 'bankofamerica.com',
  'wells fargo': 'wellsfargo.com',
  'goldman sachs': 'goldmansachs.com',
  'morgan stanley': 'morganstanley.com',
  'citibank': 'citi.com',
  'capital one': 'capitalone.com',
  'american express': 'americanexpress.com',
  'fidelity': 'fidelity.com',
  'charles schwab': 'schwab.com',
  'vanguard': 'vanguard.com',
  
  // Healthcare
  'unitedhealth': 'unitedhealthgroup.com',
  'cvs': 'cvs.com',
  'walgreens': 'walgreens.com',
  'kaiser': 'kaiserpermanente.org',
  'anthem': 'anthem.com',
  'cigna': 'cigna.com',
  'humana': 'humana.com',
  'aetna': 'aetna.com',
  
  // Retail
  'walmart': 'walmart.com',
  'target': 'target.com',
  'costco': 'costco.com',
  'home depot': 'homedepot.com',
  'lowes': 'lowes.com',
  'kroger': 'kroger.com',
  'starbucks': 'starbucks.com',
  'mcdonalds': 'mcdonalds.com',
  
  // Delivery/Gig
  'doordash': 'doordash.com',
  'instacart': 'instacart.com',
  'grubhub': 'grubhub.com',
  'postmates': 'postmates.com',
  'shipt': 'shipt.com',
  'fiverr': 'fiverr.com',
  'upwork': 'upwork.com',
  
  // Other
  'att': 'att.com',
  'verizon': 'verizon.com',
  't-mobile': 't-mobile.com',
  'comcast': 'comcast.com',
  'disney': 'disney.com',
  'fedex': 'fedex.com',
  'ups': 'ups.com',
  'usps': 'usps.com',
};
const EMPLOYER_LOGO_OPTIONS = { domainMap: EMPLOYER_DOMAIN_MAP };



// Helper to calculate monthly equivalent
function calculateMonthlyAmount(amount, frequency) {
  const numAmount = parseFloat(amount) || 0;
  switch (frequency) {
    case 'weekly':
      return numAmount * 52 / 12;
    case 'biweekly':
      return numAmount * 26 / 12;
    case 'semimonthly':
      return numAmount * 2;
    case 'monthly':
      return numAmount;
    default:
      return numAmount;
  }
}

// Helper to get frequency label
function getFrequencyLabel(frequency) {
  const labels = {
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    semimonthly: 'Twice a month',
    monthly: 'Monthly'
  };
  return labels[frequency] || frequency;
}

// Helper to get initials from name
function getInitials(firstName, lastName) {
  const first = (firstName || '').charAt(0).toUpperCase();
  const last = (lastName || '').charAt(0).toUpperCase();
  return first + last || '?';
}

// Helper to generate consistent color from name
function getAvatarColor(name) {
  const colors = [
    '#3b82f6', // blue
    '#22c55e', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Member Avatar Component
function MemberAvatar({ member, size = 24 }) {
  const initials = getInitials(member.first_name, member.last_name);
  const bgColor = getAvatarColor(`${member.first_name}${member.last_name}`);
  
  return (
    <div 
      className="member-avatar" 
      style={{ 
        width: size, 
        height: size, 
        backgroundColor: bgColor,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: size * 0.45,
        fontWeight: 600,
        flexShrink: 0
      }}
    >
      {initials}
    </div>
  );
}

// Income Add/Edit Form Component
function IncomeForm({ income, onClose, onSave, saving, members = [], isInHousehold = false, currentUserId }) {
  const [formData, setFormData] = useState({
    income_name: income?.income_name || '',
    amount: income?.amount?.toString() || '',
    pay_frequency: income?.pay_frequency || 'biweekly',
    next_pay_date: income?.next_pay_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    notes: income?.notes || '',
    icon_url: income?.icon_url || '',
    user_id: income?.user_id?.toString() || currentUserId?.toString() || '',
    is_active: income?.is_active !== undefined ? income.is_active : true,
    share_with_household: income?.share_with_household || false
  });

  // Logo auto-fetch hook
  const logoState = useLogoFetch(formData.income_name, EMPLOYER_LOGO_OPTIONS);
  const fileInputRef = useRef(null);
  const pickerRef = useRef(null);

  // Sync logo URL to form data when it changes (only if not already set)
  useEffect(() => {
    if (logoState.logoUrl && !formData.icon_url) {
      setFormData(prev => ({ ...prev, icon_url: logoState.logoUrl }));
    }
  }, [logoState.logoUrl, formData.icon_url]);

  // Set logo URL from existing income on edit
  useEffect(() => {
    if (income?.icon_url) {
      logoState.setLogoUrl(income.icon_url);
    }
  }, [income?.icon_url, logoState]);

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        logoState.setShowPicker(false);
      }
    };

    if (logoState.showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [logoState.showPicker, logoState]);

  const handleManualUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2000000) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      logoState.setLogoUrl(reader.result);
      setFormData(prev => ({ ...prev, icon_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    logoState.setLogoUrl(null);
    setFormData(prev => ({ ...prev, icon_url: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Use logo URL from state if not already in form data
    const iconUrl = formData.icon_url || logoState.logoUrl || null;
    
    const submitData = {
      income_name: formData.income_name,
      amount: parseFloat(formData.amount),
      pay_frequency: formData.pay_frequency,
      next_pay_date: formData.next_pay_date,
      notes: formData.notes || null,
      icon_url: iconUrl,
      is_active: formData.is_active,
      share_with_household: formData.share_with_household
    };
    
    // Include user_id if in household
    if (isInHousehold && formData.user_id) {
      submitData.user_id = parseInt(formData.user_id);
    }
    
    onSave(submitData);
  };

  const canSubmit = formData.income_name && formData.amount && parseFloat(formData.amount) > 0 && formData.next_pay_date;

  // Calculate preview monthly amount
  const monthlyPreview = calculateMonthlyAmount(formData.amount || 0, formData.pay_frequency);

  const currentLogoUrl = formData.icon_url || logoState.logoUrl;

  return (
    <form id="income-form" onSubmit={handleSubmit}>
      <div className="modal-body">
        {/* Income Name with Logo */}
        <div className="form-group">
          <label htmlFor="income_name">Income Name</label>
          <div className="bill-name-row">
            {/* Logo Preview */}
            <div className="bill-logo-preview" ref={pickerRef}>
              {logoState.loading ? (
                <div className="logo-loading">
                  <RefreshCw size={16} className="spinning" />
                </div>
              ) : currentLogoUrl ? (
                <div className="logo-thumbnail-wrapper">
                  <div 
                    className={`logo-thumbnail ${logoState.logoOptions.length > 1 ? 'has-options' : ''}`}
                    onClick={() => logoState.logoOptions.length > 1 && logoState.setShowPicker(!logoState.showPicker)}
                    title={logoState.logoOptions.length > 1 ? 'Click to choose a different logo' : ''}
                  >
                    <img src={currentLogoUrl} alt="" />
                    {logoState.logoOptions.length > 1 && (
                      <div className="logo-options-indicator">
                        <ChevronDown size={12} />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="logo-remove-btn"
                    onClick={handleRemoveLogo}
                    title="Remove logo"
                  >
                    <X size={12} />
                  </button>
                  
                  {/* Logo Options Picker */}
                  {logoState.showPicker && logoState.logoOptions.length > 1 && (
                    <div className="logo-picker-dropdown">
                      <div className="logo-picker-header">Choose a logo</div>
                      <div className="logo-picker-grid">
                        {logoState.logoOptions.map((option, index) => (
                          <button
                            key={option.domain || index}
                            type="button"
                            className={`logo-picker-option ${option.dataUrl === currentLogoUrl ? 'selected' : ''}`}
                            onClick={() => {
                              logoState.selectOption(option);
                              setFormData(prev => ({ ...prev, icon_url: option.dataUrl }));
                            }}
                            title={option.name || option.domain}
                          >
                            <img src={option.dataUrl} alt={option.name || option.domain} />
                            <span className="logo-picker-label">{option.name || option.domain}</span>
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="logo-picker-upload"
                        onClick={() => {
                          logoState.setShowPicker(false);
                          fileInputRef.current?.click();
                        }}
                      >
                        <Image size={14} />
                        <span>Upload custom</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  className="logo-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload logo"
                >
                  <Image size={24} />
                  <span>Upload</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleManualUpload}
                style={{ display: 'none' }}
              />
            </div>
            
            {/* Name Input */}
            <div className="bill-name-input-wrapper">
              <TextInput
                id="income_name"
                value={formData.income_name}
                onChange={(e) => setFormData({ ...formData, income_name: e.target.value, icon_url: '' })}
                placeholder="e.g., Acme Corp, Side Hustle"
                disabled={saving}
                required
              />
            </div>
          </div>
          {logoState.error && !currentLogoUrl && formData.income_name && (
            <p className="field-hint logo-hint">
              <span>Couldn't find logo automatically.</span>
              <button type="button" className="retry-link" onClick={logoState.retry}>Retry</button>
              <span> or </span>
              <button type="button" className="retry-link" onClick={() => fileInputRef.current?.click()}>upload manually</button>
            </p>
          )}
        </div>

        {/* Member Selector (Household only) */}
        {isInHousehold && members.length > 0 && (
          <div className="form-group">
            <label htmlFor="user_id">Whose Income?</label>
            <CustomSelect
              name="user_id"
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              disabled={saving}
              options={members.map(m => ({
                value: m.id.toString(),
                label: `${m.first_name} ${m.last_name}${m.id === currentUserId ? ' (You)' : ''}`,
                member: m
              }))}
              renderOption={(option) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <MemberAvatar member={option.member} size={28} />
                  <span>{option.label}</span>
                </div>
              )}
              renderSelected={(option) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <MemberAvatar member={option.member} size={24} />
                  <span>{option.label}</span>
                </div>
              )}
            />
          </div>
        )}

        {/* Personal vs Household Toggle */}
        {isInHousehold && (
          <div className="form-group">
            <label>Visibility</label>
            <div className="ownership-toggle">
              <button
                type="button"
                className={`ownership-option ${!formData.share_with_household ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, share_with_household: false })}
                disabled={saving}
              >
                <User size={18} />
                <div>
                  <span className="ownership-label">Personal</span>
                  <span className="ownership-desc">Only you can see this</span>
                </div>
              </button>
              <button
                type="button"
                className={`ownership-option ${formData.share_with_household ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, share_with_household: true })}
                disabled={saving}
              >
                <Home size={18} />
                <div>
                  <span className="ownership-label">Household</span>
                  <span className="ownership-desc">Shared with all members</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Amount */}
        <div className="form-group">
          <label htmlFor="amount">Amount Per Paycheck</label>
          <CurrencyInput
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            placeholder="0.00"
            disabled={saving}
            required
          />
        </div>

        {/* Pay Frequency */}
        <div className="form-group">
          <label htmlFor="pay_frequency">Pay Frequency</label>
          <CustomSelect
            name="pay_frequency"
            value={formData.pay_frequency}
            onChange={(e) => setFormData({ ...formData, pay_frequency: e.target.value })}
            disabled={saving}
            options={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'biweekly', label: 'Every 2 weeks' },
              { value: 'semimonthly', label: 'Twice a month (1st & 15th)' },
              { value: 'monthly', label: 'Monthly' }
            ]}
          />
        </div>

        {/* Next Pay Date */}
        <div className="form-group">
          <label htmlFor="next_pay_date">Next Pay Date</label>
          <DatePicker
            name="next_pay_date"
            value={formData.next_pay_date}
            onChange={(e) => setFormData({ ...formData, next_pay_date: e.target.value })}
            disabled={saving}
          />
          <span className="form-hint">Your next expected payday</span>
        </div>

        {/* Active Toggle - Only show when editing */}
        {income && (
          <div className="form-group">
            <div className="toggle-row">
              <div className="toggle-row-label">
                <label>Active</label>
                <span className="form-hint">Inactive income won't appear in calculations or calendar</span>
              </div>
              <Toggle
                checked={formData.is_active}
                onChange={(checked) => setFormData({ ...formData, is_active: checked })}
                disabled={saving}
              />
            </div>
          </div>
        )}

        {/* Monthly Preview */}
        {formData.amount && parseFloat(formData.amount) > 0 && (
          <div className="info-box">
            <DollarSign size={18} />
            <div>
              <strong>Estimated Monthly Income</strong>
              <p>{formatAmount(monthlyPreview)}</p>
            </div>
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={saving || !canSubmit}
        >
          {saving ? (
            <><Loader2 size={16} className="spinner" /> Saving...</>
          ) : (
            <><CheckCircle size={16} /> {income ? 'Save Changes' : 'Add Income'}</>
          )}
        </button>
      </div>
    </form>
  );
}

function ManageIncomeModal({ isOpen, onClose, onDataChange, editIncomeId = null }) {
  const { success: showSuccess, error: showError } = useToast();
  const [incomeList, setIncomeList] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isInHousehold = !!currentUser.household_id;

  // Load income data and members
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [incomeRes, membersRes] = await Promise.all([
        incomeAPI.getAll(isInHousehold), // Load household income if in household
        isInHousehold ? householdAPI.getMembers() : Promise.resolve({ data: [] })
      ]);
      setIncomeList(incomeRes.data || []);
      setMembers(membersRes.data || []);
      
      // If editIncomeId was provided, find and set the income for editing
      if (editIncomeId && incomeRes.data) {
        const incomeToEdit = incomeRes.data.find(i => i.id === editIncomeId);
        if (incomeToEdit) {
          setEditingIncome(incomeToEdit);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      showError('Failed to load income data');
    } finally {
      setLoading(false);
    }
  }, [showError, isInHousehold, editIncomeId]);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, loadData]);

  // Calculate total monthly income
  const totalMonthlyIncome = incomeList
    .filter(i => i.is_active)
    .reduce((sum, income) => sum + calculateMonthlyAmount(income.amount, income.pay_frequency), 0);

  // Handle add/edit income
  const handleSaveIncome = async (formData) => {
    setSaving(true);
    try {
      if (editingIncome) {
        await incomeAPI.update(editingIncome.id, formData);
        showSuccess('Income updated successfully');
      } else {
        await incomeAPI.create(formData);
        showSuccess('Income added successfully');
      }
      setShowAddForm(false);
      setEditingIncome(null);
      await loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Failed to save income:', error);
      showError(error.response?.data?.error || 'Failed to save income');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDeleteIncome = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await incomeAPI.delete(deleteConfirm.id);
      showSuccess('Income deleted successfully');
      setDeleteConfirm(null);
      await loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Failed to delete income:', error);
      showError('Failed to delete income');
    } finally {
      setDeleting(false);
    }
  };


  const activeCount = incomeList.filter(i => i.is_active).length;

  // Render form view
  if (showAddForm || editingIncome) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={() => {
          setShowAddForm(false);
          setEditingIncome(null);
        }}
        title={editingIncome ? 'Edit Income' : 'Add Income'}
        icon={<DollarSign size={20} />}
        size="medium"
      >
        <IncomeForm
          income={editingIncome}
          onClose={() => {
            setShowAddForm(false);
            setEditingIncome(null);
          }}
          onSave={handleSaveIncome}
          saving={saving}
          members={members}
          isInHousehold={isInHousehold}
          currentUserId={currentUser.id}
        />
      </BaseModal>
    );
  }

  // Render delete confirmation
  if (deleteConfirm) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Income"
        icon={<AlertTriangle size={20} className="modal-icon danger" />}
        size="small"
      >
        <div className="modal-body">
          <p>Are you sure you want to delete <strong>{deleteConfirm.income_name}</strong>?</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>This action cannot be undone.</p>
        </div>
        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={() => setDeleteConfirm(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleDeleteIncome}
            disabled={deleting}
          >
            {deleting ? (
              <><Loader2 size={16} className="spinner" /> Deleting...</>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </BaseModal>
    );
  }

  // Main list view
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Income"
      icon={<Wallet size={20} />}
      size="large"
    >
      <div className="modal-body">
        <div className="manage-income-content">
          {loading ? (
            <div className="loading-state">
              <Loader2 size={24} className="spinner" />
              <span>Loading income data...</span>
            </div>
          ) : incomeList.length === 0 ? (
            <div className="income-empty-state">
              <Wallet size={48} />
              <h3>No income sources yet</h3>
              <p>Add your income to track paydays on your calendar and see how much of your income goes to bills.</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                <Plus size={16} /> Add Your First Income
              </button>
            </div>
          ) : (
            <>
              {/* Summary Header */}
              <div className="income-summary-header">
                <div className="income-summary-info">
                  <div className="income-summary-icon">
                    <DollarSign size={24} />
                  </div>
                  <div>
                    <h3>{formatAmount(totalMonthlyIncome)}</h3>
                    <p>estimated monthly income from {activeCount} source{activeCount !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus size={16} /> Add Income
                </button>
              </div>

              {/* Income List */}
              <div className="income-list">
                {incomeList.map((income) => {
                  const monthlyAmount = calculateMonthlyAmount(income.amount, income.pay_frequency);
                  return (
                    <div 
                      key={income.id} 
                      className={`income-item ${!income.is_active ? 'inactive' : ''}`}
                    >
                      <div className="income-item-icon">
                        {income.icon_url ? (
                          <img src={income.icon_url} alt="" className="income-logo" />
                        ) : (
                          <DollarSign size={20} />
                        )}
                      </div>
                      <div className="income-item-info">
                        <div className="income-item-header">
                          <h4>{income.income_name}</h4>
                          <div className="income-item-badges">
                            {/* Owner badge for household */}
                            {isInHousehold && income.first_name && (
                              <span className="badge owner">
                                {income.user_id === currentUser.id 
                                  ? 'You' 
                                  : `${income.first_name}`}
                              </span>
                            )}
                            {!income.is_active && (
                              <span className="badge inactive">Inactive</span>
                            )}
                          </div>
                        </div>
                        <p className="income-item-frequency">
                          {getFrequencyLabel(income.pay_frequency)} · Next: {formatDate(income.next_pay_date)}
                        </p>
                        <p className="income-item-amount">
                          {formatAmount(income.amount)}/paycheck · <span className="monthly">{formatAmount(monthlyAmount)}/mo</span>
                        </p>
                      </div>
                      <div className="income-item-actions">
                        {isInHousehold && (
                          <OwnershipBadge 
                            isHousehold={income.share_with_household} 
                            variant="icon-only" 
                            size="sm" 
                          />
                        )}
                        <button
                          className="btn btn-icon"
                          onClick={() => setEditingIncome(income)}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="btn btn-icon btn-icon-danger"
                          onClick={() => setDeleteConfirm(income)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </BaseModal>
  );
}

export default ManageIncomeModal;
